import Async from "components/DesignSystem/AsyncComponents/Async";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { TableUI } from "components/design/TableUI";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import {
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  useGetCOAImportStateQuery,
  useUpdateCOAMappingMutation,
} from "store/apis/chartOfAccounts";
import { TxnCategories, UploadCOAFileResponse } from "types/Models/books";
import { ImportCOAHeader } from "./ImportCOA";
import classNames from "classnames";
import ArrowBendDownRight from "static/images/ArrowBendDownRight.svg";
import { ReactNode, useMemo } from "react";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { useFormikContext } from "formik";
import { MultiValue, SingleValue } from "react-select";
import { BackendError } from "types/utils/error";
import { useToast } from "hooks/useToast";
import { RightArrow } from "components/icons/RightArrow";
import {
  IMPORT_SOURCE,
  IMPORT_SOURCE_LABEL,
  ImportSource,
} from "constants/qboImportSteps";

type MapCategoriesStepProps = {
  onNext: () => void;
  importProcessId: string;
  entityId: string;
  header?: ReactNode;
  initialValues?: FormValues;
  onFormValuesChange?: (values: FormValues) => void;
  additionalFooterButtons?: ReactNode;
  source?: ImportSource;
  shouldDefaultUnmatchedToNone?: boolean;
};

export type CategoryMapping = {
  name: string;
  opening_balance: number;
};

export type FormValues = Record<string, CategoryMapping>;

const NONE_OPTION_VALUE = "NONE";

const columnHelper = createColumnHelper<TxnCategories>();

const ImportedCategory = ({
  parsedAccounts,
  category,
}: {
  parsedAccounts: UploadCOAFileResponse["parsed_accounts"];
  category: TxnCategories;
}) => {
  const { setFieldValue, values } = useFormikContext<FormValues>();

  const options = useMemo(
    () => [
      {
        label: (
          <>
            None{" "}
            <i className="t-text-text-30">
              (Doesn't exist in uploaded COA file)
            </i>
          </>
        ),
        value: NONE_OPTION_VALUE,
        opening_balance: 0,
      },
      ...parsedAccounts.map((account) => ({
        label: account.name,
        value: account.name,
        opening_balance: account.opening_balance || 0,
        isDisabled: Object.values(values).some(
          (mapping) => mapping.name === account.name
        ),
      })),
    ],
    [parsedAccounts, values]
  );

  const currentValue = values[category.identifier];
  const selectedOption =
    options.find((opt) => opt.value === currentValue?.name) || null;

  const handleChange = (
    selection: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (selection instanceof Array) {
      return;
    }

    if (!selection?.value) {
      return;
    }

    setFieldValue(String(category.identifier), {
      name: selection?.value,
      // @ts-ignore
      opening_balance: Number(selection?.opening_balance) || 0,
    });
  };

  const currentIndex = Object.keys(values).indexOf(String(category.identifier));

  return (
    <Combobox
      options={options}
      value={selectedOption}
      placeholder="Select"
      size="small"
      menuPortalTarget={document.body}
      onChange={handleChange}
      isClearable={false}
      menuPlacement={currentIndex < 3 ? "bottom" : "top"}
    />
  );
};

const columns = [
  columnHelper.accessor("name", {
    header: "Inkle category",
    size: 50,
    cell: ({ row, getValue }) => {
      return (
        <div
          style={{
            paddingLeft: `${row.depth * 1}rem`,
          }}
          className={classNames(
            "t-w-full t-flex t-justify-between t-items-center t-gap-1",
            {
              "t-text-subtext t-text-text-100 t-my-3": row.depth === 0,
              "t-text-body-sm t-text-text-60": row.depth > 0,
            }
          )}
        >
          <div>
            {row.depth > 0 && (
              <img
                src={ArrowBendDownRight}
                alt="ArrowBendDownRight"
                className="t-size-4 t-mr-1 -t-mt-1"
              />
            )}
            {getValue()}
          </div>
          {row.depth > 0 && (
            <span className="t-text-neutral-30">
              <RightArrow />
            </span>
          )}
        </div>
      );
    },
  }),

  columnHelper.accessor("identifier", {
    header: () => <Label required>Imported category</Label>,
    size: 50,
    cell: (info) => {
      const parsedAccounts = (
        info.table.options.meta as {
          parsedAccounts: UploadCOAFileResponse["parsed_accounts"];
        }
      )?.parsedAccounts;

      if (info.row.depth === 0) {
        return null;
      }

      return (
        <ImportedCategory
          parsedAccounts={parsedAccounts}
          category={info.row.original}
        />
      );
    },
  }),
];

const MapTable = ({
  parsedAccounts,
}: {
  parsedAccounts: UploadCOAFileResponse["parsed_accounts"];
}) => {
  const { chartOfAccounts } = useChartOfAccounts({
    hiddenCategoryTypes: ["BANK_ACCOUNT"],
  });

  const filteredChartOfAccounts = useMemo(() => {
    const filterSystemCategories = (
      accounts: typeof chartOfAccounts
    ): typeof chartOfAccounts => {
      return accounts
        .filter((account) => account.is_system_category)
        .map((account) => ({
          ...account,
          types: account.types
            ? filterSystemCategories(account.types)
            : undefined,
        }));
    };

    return filterSystemCategories(chartOfAccounts);
  }, [chartOfAccounts]);

  const table = useReactTable({
    columns,
    data: filteredChartOfAccounts,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: (row) => row.uuid,
    getSubRows: (row) => row.types,
    state: {
      expanded: true,
    },
    meta: {
      parsedAccounts,
    },
  });

  return (
    <TableUI
      table={table}
      contentClassName="sm:t-min-w-[600px]"
      size="small"
      sticky={false}
      isHeaderCellUpperCase={false}
    />
  );
};

export const MapCategoriesStepContent = ({
  onNext,
  importProcessId,
  entityId,
  header,
  initialValues: providedInitialValues,
  onFormValuesChange,
  additionalFooterButtons,
  source = IMPORT_SOURCE.QBO,
  shouldDefaultUnmatchedToNone = false,
}: Omit<MapCategoriesStepProps, "header"> & { header: ReactNode }) => {
  const { alertToast } = useToast();
  const [updateCOAMap] = useUpdateCOAMappingMutation();
  const { data: coaImportData, ...getCOAImportState } =
    useGetCOAImportStateQuery(
      { entityId, coaImportStateId: importProcessId },
      { skip: !entityId || !importProcessId }
    );

  const { chartOfAccounts, ...apiStates } = useChartOfAccounts({
    hiddenCategoryTypes: ["BANK_ACCOUNT"],
  });

  const defaultInitialValues = useMemo(() => {
    const flatten = (accounts: typeof chartOfAccounts, level: number = 0) => {
      return accounts.reduce((acc, account) => {
        if (account.is_system_category && level > 0) {
          acc.push(account);
        }
        if (
          account.types &&
          account.types.length > 0 &&
          account.is_system_category
        ) {
          acc.push(...flatten(account.types, level + 1));
        }
        return acc;
      }, [] as typeof chartOfAccounts);
    };

    const unmatchedName = shouldDefaultUnmatchedToNone ? NONE_OPTION_VALUE : "";

    return flatten(chartOfAccounts).reduce((acc, coa) => {
      const matchingParsedAccount = coaImportData?.parsed_accounts.find(
        (item) => item.name.toLowerCase() === coa.name.toLowerCase()
      );
      acc[coa.identifier] = {
        name: matchingParsedAccount?.name || unmatchedName,
        opening_balance: matchingParsedAccount?.opening_balance || 0,
      };
      return acc;
    }, {} as FormValues);
  }, [
    chartOfAccounts,
    coaImportData?.parsed_accounts,
    shouldDefaultUnmatchedToNone,
  ]);

  const initialValues = providedInitialValues || defaultInitialValues;

  const handleSubmit = async (values: FormValues) => {
    const unmappedCategories = Object.entries(values).filter(
      ([key, value]) => !value.name || value.name === ""
    );

    if (unmappedCategories.length > 0) {
      alertToast({
        message: "Please map all required categories before proceeding.",
      });
      return;
    }

    try {
      const transactionCategoriesMap = Object.entries(values)
        .filter(([key, value]) => value.name !== NONE_OPTION_VALUE)
        .map(([key, value]) => ({
          [Number(key)]: {
            name: value.name.toLocaleUpperCase(),
            opening_balance: value.opening_balance || 0,
          },
        }));

      await updateCOAMap({
        entityId,
        coaImportStateId: importProcessId,
        transactionCategoriesMap,
      }).unwrap();

      if (onFormValuesChange) {
        onFormValuesChange(values);
      }

      onNext();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  return (
    <FormikForm<FormValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      enableReinitialize
    >
      {({ isSubmitting, isValid, submitForm }) => {
        return (
          <>
            {header}
            <Async.Root
              isLoading={getCOAImportState.isLoading || apiStates.isLoading}
              isSuccess={getCOAImportState.isSuccess && apiStates.isSuccess}
              isEmpty={coaImportData?.parsed_accounts?.length === 0}
              renderOnLoad={
                <Modal.Body className="t-flex t-flex-col t-gap-1 sm:t-px-4">
                  <Skeleton className="t-h-8 t-rounded-none" />
                  <Skeleton className="t-h-8 t-rounded-none" />
                  <Skeleton className="t-h-8 t-rounded-none" />
                  <Skeleton className="t-h-8 t-rounded-none" />
                  <Skeleton className="t-h-8 t-rounded-none" />
                  <Skeleton className="t-h-8 t-rounded-none" />
                  <Skeleton className="t-h-8 t-rounded-none" />
                  <Skeleton className="t-h-8 t-rounded-none" />
                  <Skeleton className="t-h-8 t-rounded-none" />
                  <Skeleton className="t-h-8 t-rounded-none" />
                </Modal.Body>
              }
            >
              <Async.Empty>
                <Modal.Body className="t-text-center t-h-60 t-text-body-sm t-text-text-100 t-flex t-items-center t-justify-center sm:t-px-4">
                  No accounts found to map from the imported{" "}
                  {IMPORT_SOURCE_LABEL[source]} data.
                </Modal.Body>
              </Async.Empty>
              <Async.Success>
                <Modal.Body className="t-flex t-flex-col t-gap-2 t-overflow-auto t-max-h-[60vh] sm:t-px-4">
                  <p className="t-text-body-sm t-text-text-100">
                    Map categories imported from {IMPORT_SOURCE_LABEL[source]}{" "}
                    to system categories in Inkle Books. Select "None" if no
                    corresponding category exists in Inkle Books
                  </p>
                  <Banner
                    type="neutral"
                    description="Map the following key categories for accurate financial reports. All other categories will be imported unchanged."
                  />
                  <MapTable
                    parsedAccounts={coaImportData?.parsed_accounts || []}
                  />
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  {additionalFooterButtons}
                  <Modal.RawClose asChild>
                    <Button type="reset" disabled={isSubmitting}>
                      Cancel
                    </Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    disabled={isSubmitting || !isValid}
                    isLoading={isSubmitting}
                    onClick={submitForm}
                  >
                    Next
                  </Button>
                </Modal.FooterButtonGroup>
              </Async.Success>
            </Async.Root>
          </>
        );
      }}
    </FormikForm>
  );
};

export const MapCategoriesStep = ({
  onNext,
  importProcessId,
  entityId,
  header,
  initialValues,
  onFormValuesChange,
  additionalFooterButtons,
}: MapCategoriesStepProps) => {
  return (
    <Modal.Content size="xl" useCustomOverlay>
      <MapCategoriesStepContent
        onNext={onNext}
        importProcessId={importProcessId}
        entityId={entityId}
        header={header || <ImportCOAHeader />}
        initialValues={initialValues}
        onFormValuesChange={onFormValuesChange}
        additionalFooterButtons={additionalFooterButtons}
      />
    </Modal.Content>
  );
};
